import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Link2, Plus } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { PillLabel } from '../pill-label/pill-label.tsx';

const addPillVariants = cva(
  [
    'inline-flex items-center gap-1 rounded-full border border-dashed bg-surface px-2 py-0.5',
    'text-2xs font-bold tracking-wide whitespace-nowrap transition-colors',
  ],
  {
    variants: {
      variant: {
        default: 'shrink-0 border-ink-faint text-ink',
        /*
         * In the journey's colour: the click links the meeting to a step. A
         * journey type is named by staff and can be long, so the pill may
         * shrink: to `short` where there is one, else to "…". The caller puts
         * the full text in `title`.
         */
        journey: 'min-w-0 shrink border-on-pink/50 text-on-pink',
      },
      /* A label answers the card's hover; a control of its own answers only its own. */
      hover: { card: '', self: '' },
    },
    compoundVariants: [
      { variant: 'default', hover: 'card', className: 'group-hover:border-ink' },
      {
        variant: 'journey',
        hover: 'card',
        className: 'group-hover:border-on-pink group-hover:bg-pink/30',
      },
      { variant: 'default', hover: 'self', className: 'hover:border-ink' },
      {
        variant: 'journey',
        hover: 'self',
        className: 'hover:border-on-pink hover:bg-pink/30',
      },
    ],
    defaultVariants: { variant: 'default', hover: 'card' },
  },
);

type AddPillVariant = VariantProps<typeof addPillVariants>['variant'];

/* Padding, border, icon and gap around the text, in rem. */
const CHROME_REM = 2;

/*
 * With `short`, the label is a string and the pill trades it for `short` when
 * the card's pill slot has no room for it; see `PillLabel`.
 */
type AddPillLabel =
  | { short?: undefined; children?: React.ReactNode }
  | { short: string; children: string };

export type AddPillProps = Omit<React.ComponentProps<'span'>, 'style' | 'children'> &
  AddPillLabel & { variant?: AddPillVariant };

function AddPillContent({
  variant,
  short,
  children,
}: {
  variant: AddPillVariant;
  short?: string;
  children: React.ReactNode;
}) {
  const Icon = variant === 'journey' ? Link2 : Plus;
  let label = children;
  if (short !== undefined && typeof children === 'string') {
    label = <PillLabel label={children} short={short} chromeRem={CHROME_REM} />;
  } else if (variant === 'journey') label = <span className='truncate'>{children}</span>;

  return (
    <>
      <Icon className='size-2.5 shrink-0 stroke-3' aria-hidden='true' />
      {label}
    </>
  );
}

/**
 * What a card that is not in Wundo yet wears where a card that is wears its
 * kind: a dashed pill saying what the click will do — a plus to add it, a link
 * to put it on the journey step Wundo thinks it belongs to.
 *
 * Visible at rest, never on hover only. Here the whole card is the button and
 * this is the label on it; where the pill does something the card's click does
 * not, it is an `AddPillButton`.
 */
export function AddPill({ className, variant, short, children, ...props }: AddPillProps) {
  return (
    <span
      data-slot='add-pill'
      data-variant={variant ?? 'default'}
      className={cn(addPillVariants({ variant }), className)}
      {...props}
    >
      <AddPillContent variant={variant} short={short}>
        {children}
      </AddPillContent>
    </span>
  );
}

export type AddPillButtonProps = Omit<
  React.ComponentProps<'button'>,
  'style' | 'type' | 'children'
> &
  AddPillLabel & { variant?: AddPillVariant };

/*
 * The same offer as a control of its own, for a card whose click already means
 * something else — a meeting in Wundo opens, so linking it to a journey step
 * needs its own button. Put it in a card's `action` slot, not inside a button.
 */
export function AddPillButton({
  className,
  variant,
  short,
  children,
  ...props
}: AddPillButtonProps) {
  return (
    <button
      type='button'
      data-slot='add-pill'
      data-variant={variant ?? 'default'}
      className={cn(
        addPillVariants({ variant, hover: 'self' }),
        'max-w-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    >
      <AddPillContent variant={variant} short={short}>
        {children}
      </AddPillContent>
    </button>
  );
}
